import { MonitorStatus } from '@fe/core';
import { CompositeFilterDescriptor, DataResult, FilterOperator, SortDescriptor, State } from '@progress/kendo-data-query';
import { Grid, GridColumn, GridDataStateChangeEvent, GridHandle, GridNoRecords } from '@progress/kendo-react-grid';
import React from 'react';
import { FlightStatus } from '../../../../../common/enums';
import { useGotoTargetMaps } from '../../../../../hooks/use-goto-target-maps';
import { MessageType } from '../../../../../services/actor-messages';
import { FleetMonitorSortType, SortOrderEnum } from '../../../../../types/settings';
import { MyFleetAirportModeEnum, MyFleetMode } from '../../../../../udView/udview';
import { useMultiWindowContext } from '../../../../components/multi-window/context/multi-window-context';
import { EventBus, useEvent } from '../../../../contexts/event-bus';
import { useFocusedWorkspaceItemContext } from '../../../../contexts/workspace/workspace-item-context';
import { useAppSelector } from '../../../../hooks/store-hooks';
import { useFlightWorker } from '../../../../hooks/use-flight-worker';
import './FleetMonitor.css';
import { FleetMonitorItem } from './FleetMonitorItem';

export function FleetMonitorList() {
    const myFleetMode = useAppSelector((x) => x.settings.myFleetMode);
    const airportMode = useAppSelector((x) => x.settings.myFleetAirportMode);
    const splitView = useAppSelector((x) => x.settings.fleetMonitorAirportSplitView);

    if (myFleetMode === MyFleetMode.airport && airportMode === MyFleetAirportModeEnum.departureArrivals && splitView) {
        return (
            <div className="h-100">
                <div className="h-50 d-flex flex-column">
                    <div className="text-center border-bottom secondary-bg-color" style={{ fontSize: '0.8rem', padding: '0.125rem 0' }}>
                        Departures
                    </div>
                    <div className="flex-grow-1 min-h-0">
                        <FleetMonitorItemsList mode={MyFleetAirportModeEnum.departures} />
                    </div>
                </div>

                <div className="h-50 d-flex flex-column">
                    <div
                        className="text-center border-bottom border-top secondary-bg-color"
                        style={{ fontSize: '0.8rem', padding: '0.125rem 0' }}>
                        Arrivals
                    </div>
                    <div className="flex-grow-1 min-h-0">
                        <FleetMonitorItemsList mode={MyFleetAirportModeEnum.arrivals} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <FleetMonitorItemsList mode={MyFleetAirportModeEnum.departureArrivals} />
        </div>
    );
}

function FleetMonitorItemsList(props: { mode: MyFleetAirportModeEnum }) {
    const multiWindowContext = useMultiWindowContext();
    const worker = useFlightWorker();
    const context = useFocusedWorkspaceItemContext();
    const myFleetMode = useAppSelector((x) => x.settings.myFleetMode);
    const show5thRow = useAppSelector((x) => x.settings.fleetMonitorShow5thDataRow);
    const showFlightBgColor = useAppSelector((x) => x.settings.fleetMonitorFlightStatusColorBackground);
    const sortBy = useFleetMonitorSort();
    const sortDir = useAppSelector((x) => x.settings.fleetMonitorSortOrder);
    const minimumMonitorLevel = useAppSelector((x) => x.settings.fleetMonitorMinimumLevel);
    const collapsedView = useAppSelector((x) => x.settings.fleetMonitorCollapsedView);
    const showPlanned = useAppSelector((x) => x.settings.fleetMonitorShowPlannedFlights);
    const showScheduled = useAppSelector((x) => x.settings.fleetMonitorShowScheduledFlights);
    const [state, setState] = React.useState<State>({ take: 50, skip: 0 });
    const [dataResult, setDataResult] = React.useState<DataResult>({ total: 0, data: [] });
    const grid = React.useRef<GridHandle | null>(null);
    const { goToFlight } = useGotoTargetMaps();

    const filterAndSort = React.useMemo(
        () =>
            ({
                filter: getFilter(props.mode, minimumMonitorLevel, showPlanned, showScheduled),
                sort: getSort(sortBy, sortDir, props.mode, myFleetMode)
            }) satisfies Pick<State, 'filter' | 'sort'>,
        [sortBy, sortDir, minimumMonitorLevel, props.mode, showPlanned, showScheduled, myFleetMode]
    );

    const loadData = React.useCallback(
        async (state: State) => {
            const result = await worker.actor.sendAsync({ type: MessageType.queryFleetMonitor, data: state });
            setState(state);
            setDataResult(result);
        },
        [worker]
    );

    // Reload when filter and sort change
    React.useEffect(() => {
        loadData({ ...state, ...filterAndSort });
    }, [loadData, filterAndSort]);

    useEvent(
        'update-end',
        React.useCallback(() => {
            if (!grid.current) {
                return;
            }
            loadData({
                take: grid.current.props.take,
                skip: grid.current.props.skip,
                filter: grid.current.props.filter,
                sort: grid.current.props.sort
            });
        }, [loadData])
    );

    const handleDataStateChanged = (e: GridDataStateChangeEvent) => loadData(e.dataState);

    const onClick = React.useCallback(
        (id: number) => {
            goToFlight(id, { source: 'fleet-monitor' });
        },
        [context]
    );

    const onContextMenu = React.useCallback(
        (event: MouseEvent, id: number) => {
            EventBus.fireContextMenu([{ kind: 'flight', flightIds: [id] }], event);
        },
        [context, multiWindowContext]
    );

    const RowComponent = React.useMemo(
        () => FleetMonitorItem({ show5thRow, showFlightBgColor, collapsedView, onClick, onContextMenu }),
        [show5thRow, showFlightBgColor, collapsedView, onClick, onContextMenu]
    );

    return (
        <Grid
            ref={grid}
            className="k-grid-no-header k-grid-no-borders h-100"
            data={dataResult}
            dataItemKey="id"
            rowHeight={collapsedView ? 32 : show5thRow ? 65 : 55}
            onDataStateChange={handleDataStateChanged}
            scrollable="virtual"
            filter={filterAndSort.filter}
            sort={filterAndSort.sort}
            rows={{
                data: RowComponent
            }}
            {...state}>
            <GridNoRecords>
                <div></div>
            </GridNoRecords>
            <GridColumn id="main" field="df" />
        </Grid>
    );
}

function getFilter(
    mode: MyFleetAirportModeEnum,
    minimumMonitorLevel: MonitorStatus,
    showPlanned: boolean,
    showScheduled: boolean
): CompositeFilterDescriptor {
    const filter: CompositeFilterDescriptor = {
        logic: 'and',
        filters: []
    };

    if (minimumMonitorLevel > MonitorStatus.null) {
        filter.filters.push({
            field: 'monitorStatus',
            operator: FilterOperator.GreaterThanOrEqual,
            value: minimumMonitorLevel
        });
    }

    if (!showScheduled) {
        filter.filters.push({
            field: 'status',
            operator: FilterOperator.NotEqualTo,
            value: FlightStatus.Scheduled
        });
    }

    if (!showPlanned) {
        filter.filters.push({
            field: 'status',
            operator: FilterOperator.NotEqualTo,
            value: FlightStatus.Planned
        });
    }

    if (mode === MyFleetAirportModeEnum.arrivals) {
        filter.filters.push({
            field: 'myFleetMatch',
            operator: FilterOperator.EqualTo,
            value: 1
        });
    }

    if (mode === MyFleetAirportModeEnum.departures) {
        filter.filters.push({
            field: 'myFleetMatch',
            operator: FilterOperator.EqualTo,
            value: 2
        });
    }

    return filter;
}

function getSort(
    sort: FleetMonitorSortType,
    sortDir: SortOrderEnum,
    airportMode: MyFleetAirportModeEnum,
    myFleetMode: MyFleetMode
): SortDescriptor[] {
    const dir = sortDir === SortOrderEnum.asc ? 'asc' : 'desc';
    switch (sort) {
        case FleetMonitorSortType.monitorStatusPriorityCallsignFlightStatus:
            return [
                { field: 'monitorStatus', dir },
                { field: 'monitorPriority', dir },
                { field: 'normalizedAcid', dir },
                { field: 'status', dir }
            ];
        case FleetMonitorSortType.callsignFlightStatus:
            return [
                { field: 'normalizedAcid', dir },
                { field: 'status', dir }
            ];
        case FleetMonitorSortType.flightStatusCallsign:
            return [
                { field: 'status', dir },
                { field: 'normalizedAcid', dir }
            ];
        case FleetMonitorSortType.flightStatusDepartureTime:
            return [
                { field: 'status', dir },
                { field: 'departureTime', dir }
            ];
        case FleetMonitorSortType.departureTime:
            return [{ field: 'departureTime', dir }];
        case FleetMonitorSortType.eta:
            return [{ field: 'arrivalTime', dir }];
        case FleetMonitorSortType.distanceFromDestination:
            return [{ field: 'distanceToDestination', dir }];
        case FleetMonitorSortType.departureTimeEta:
            if (myFleetMode === MyFleetMode.airport) {
                if (airportMode === MyFleetAirportModeEnum.arrivals) {
                    return [{ field: 'arrivalTime', dir }];
                } else if (airportMode === MyFleetAirportModeEnum.departures) {
                    return [{ field: 'departureTime', dir }];
                } else if (airportMode === MyFleetAirportModeEnum.departureArrivals) {
                    return [{ field: '__airportModeArrivalTimeDepartureTimeCombined__', dir }];
                }
            }
            break;
        case FleetMonitorSortType.taxiTime:
            return [{ field: 'extra.taxiTime', dir }];
    }

    return [];
}

export function useFleetMonitorSort(): FleetMonitorSortType {
    return useAppSelector((x) => {
        if (x.settings.defaultFlightMonitorSortType === FleetMonitorSortType.departureTimeEta) {
            if (x.settings.myFleetMode !== MyFleetMode.airport) {
                // Deptime & eta is only available in airport mode my fleet mode
                return FleetMonitorSortType.monitorStatusPriorityCallsignFlightStatus;
            }
        }
        return x.settings.defaultFlightMonitorSortType;
    });
}
